import { type FC, type FormEvent, useEffect, useState } from 'react'
import { Col, Container, Row, Tab } from 'react-bootstrap'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { FormSaveBar } from '../../../../components/FormSaveBar/FormSaveBar'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { useZocksAuth } from '../../../../components/ScopedBlock/hooks'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  useDeleteSchedulingEventTypeMutation,
  useDuplicateSchedulingEventTypeMutation,
  useGetAccountSchedulingSettingsQuery,
  useGetSchedulingEventTypeByIdQuery,
  useGetSchedulingLocationsQuery,
  useUpsertSchedulingEventTypeMutation,
} from '../../../../store/scheduling/scheduling'
import { type AccountSchedulingSettings, EventTypeAccess, type Location, MeetingMode, type SchedulingEventType } from '../../../../models/Scheduling'
import { ConfirmDialog } from '../../../Scheduling/shared/ConfirmDialog'
import { isSymbolicLocationKind } from '../../../Scheduling/flows/form/scheduleHelpers'
import { DetailsTab } from './tabs/DetailsTab'
import { AvailabilityTab } from './tabs/AvailabilityTab'
import { EmailTemplatesTab } from './tabs/EmailTemplatesTab'
import { RemindersTab } from './tabs/RemindersTab'
import { EventTypeSummaryCard } from './sidebar/EventTypeSummaryCard'
import { StatusSegment } from './sidebar/StatusSegment'
import { PlacementSection } from './sidebar/PlacementSection'
import { EditorActions } from './sidebar/EditorActions'

const FORM_ID = 'event-type-editor-form'
const EDITOR_TAB_KEYS = ['details', 'availability', 'email-templates', 'reminders'] as const
const DEFAULT_BACK_PATH = '/workspace/setup/scheduling'

type EditorTabKey = (typeof EDITOR_TAB_KEYS)[number]

const useEditorOrigin = (): string | undefined => {
  const location = useLocation()
  return (location.state as { origin?: string } | null)?.origin
}

interface EventTypeEditorProps {
  eventType: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  locations: Location[]
  activeTab: EditorTabKey
  disabled: boolean
  onTabChange: (tab: EditorTabKey) => void
}

const EventTypeEditor: FC<EventTypeEditorProps> = ({ eventType, accountSettings, locations, activeTab, disabled, onTabChange }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const origin = useEditorOrigin()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const [upsertEventType, { isLoading: isSaving }] = useUpsertSchedulingEventTypeMutation()
  const [duplicateEventType] = useDuplicateSchedulingEventTypeMutation()
  const [deleteEventType, { isLoading: isDeleting }] = useDeleteSchedulingEventTypeMutation()

  const [draft, setDraft] = useState<SchedulingEventType>(eventType)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [showSharedBanner, setShowSharedBanner] = useState(true)

  const isDirty = JSON.stringify(draft) !== JSON.stringify(eventType)
  const requiresLocation = draft.meetingMode === MeetingMode.IN_PERSON
  const isValid = draft.name.trim().length > 0 && (!requiresLocation || Boolean(draft.location?.id) || isSymbolicLocationKind(draft.location?.kind))

  const handleDraftChange = (patch: Partial<SchedulingEventType>) => {
    setDraft((current) => ({ ...current, ...patch }))
  }

  const handleSave = async (event: FormEvent) => {
    event.preventDefault()
    const result = await upsertEventType({ accountId, eventType: draft })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not save the event type', messages: ['Please try again.'] }))
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Event type saved', messages: [`${result.data.name} was updated.`] }))
    setDraft(result.data)
  }

  const handleDuplicate = async () => {
    const result = await duplicateEventType({ accountId, eventTypeId: eventType.id })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not duplicate the event type', messages: ['Please try again.'] }))
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Event type duplicated', messages: [`${result.data.name} was created.`] }))
    navigate(`/workspace/setup/scheduling/scheduling-event-type/edit/${result.data.id}/details`, { state: origin ? { origin } : undefined })
  }

  const handleDelete = async () => {
    const result = await deleteEventType({ accountId, eventTypeId: eventType.id })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not delete the event type', messages: ['Please try again.'] }))
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Event type deleted', messages: [`${eventType.name} was removed.`] }))
    setIsDeleteOpen(false)
    navigate(origin ?? DEFAULT_BACK_PATH)
  }

  const handlePreview = () => {
    dispatch(addToast({ variant: 'info', title: 'Not available yet', messages: ['The public booking page preview opens here once it exists.'] }))
  }

  return (
    <div className='d-flex flex-column min-vh-100'>
      <Row>
        <Col lg={8}>
          {draft.access === EventTypeAccess.ACCOUNT && !disabled && showSharedBanner ? (
            <div className='d-flex align-items-center justify-content-between gap-2 paragraph-small p-3 mb-3 border rounded-2 zocks-border-color-gray-200 zocks-bg-color-gray-100'>
              <span className='d-flex align-items-center zocks-gap-0.5'>
                <i className='bi bi-info-circle zocks-text-gray-600' />
                You&apos;re editing a shared event type. Changes apply to all users.
              </span>
              <Button variant='grayGhost' className='p-1' aria-label='Dismiss notice' onClick={() => setShowSharedBanner(false)}>
                <i className='bi bi-x-lg' />
              </Button>
            </div>
          ) : null}
          <form id={FORM_ID} onSubmit={handleSave}>
            <Tabs activeKey={activeTab} onSelect={(key) => onTabChange((key as EditorTabKey) ?? 'details')} className='mb-4'>
              <Tab eventKey='details' title='Details'>
                <DetailsTab draft={draft} disabled={disabled} onDraftChange={handleDraftChange} />
              </Tab>
              <Tab eventKey='availability' title='Availability'>
                <AvailabilityTab draft={draft} accountSettings={accountSettings} locations={locations} disabled={disabled} onDraftChange={handleDraftChange} />
              </Tab>
              <Tab eventKey='email-templates' title='Email templates'>
                <EmailTemplatesTab draft={draft} disabled={disabled} onDraftChange={handleDraftChange} />
              </Tab>
              <Tab eventKey='reminders' title='Reminders'>
                <RemindersTab draft={draft} disabled={disabled} onDraftChange={handleDraftChange} />
              </Tab>
            </Tabs>
          </form>
        </Col>
        <Col lg={4} className='d-flex flex-column gap-2'>
          <EventTypeSummaryCard draft={draft} accountSettings={accountSettings} locations={locations} disabled={disabled} onDraftChange={handleDraftChange} />
          <div className='d-flex flex-column gap-4 p-3 border rounded-3 zocks-border-color-gray-300 zocks-bg-color-white'>
            <StatusSegment isActive={draft.active} disabled={disabled} onChange={(isActive) => handleDraftChange({ active: isActive })} />
            <PlacementSection draft={draft} disabled={disabled} onDraftChange={handleDraftChange} />
          </div>
          <EditorActions eventType={draft} disabled={disabled} onPreview={handlePreview} onDuplicate={handleDuplicate} onDelete={() => setIsDeleteOpen(true)} />
        </Col>
      </Row>
      <FormSaveBar
        formId={FORM_ID}
        isVisible={isDirty}
        isSubmitting={isSaving}
        isValid={isValid}
        isDirty={isDirty}
        errors={{}}
        onDiscard={() => setDraft(eventType)}
      />
      {isDeleteOpen ? (
        <ConfirmDialog
          title='Delete event type'
          body={`Delete "${eventType.name}"? Users can no longer offer it; existing bookings are not affected.`}
          confirmLabel='Delete'
          isLoading={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setIsDeleteOpen(false)}
        />
      ) : null}
    </div>
  )
}

export const EventTypeEditorView: FC = () => {
  const { eventTypeId = '', tabType = 'details' } = useParams()
  const navigate = useNavigate()
  const origin = useEditorOrigin()
  const backPath = origin ?? DEFAULT_BACK_PATH
  const dispatch = useAppDispatch()
  const { hasScope } = useZocksAuth()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { data: eventType, isLoading, isError } = useGetSchedulingEventTypeByIdQuery({ accountId, eventTypeId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })

  const isAccountAdmin = hasScope('**', 'account_config:update', ['account_config:admin'])

  const activeTab: EditorTabKey = (EDITOR_TAB_KEYS as readonly string[]).includes(tabType) ? (tabType as EditorTabKey) : 'details'

  const handleTabChange = (tab: EditorTabKey) => {
    navigate(`/workspace/setup/scheduling/scheduling-event-type/edit/${eventTypeId}/${tab}`, { replace: true, state: origin ? { origin } : undefined })
  }

  useEffect(() => {
    if (isError) {
      dispatch(addToast({ variant: 'error', title: 'Could not load the event type', messages: ['Please try again.'] }))
    }
  }, [isError, dispatch])

  if (isError) {
    return <Navigate to={backPath} replace />
  }

  const disabled = eventType ? (eventType.access === EventTypeAccess.ACCOUNT ? !isAccountAdmin : false) : true

  return (
    <Container>
      <Row className='mb-4'>
        <Col className='d-flex align-items-center gap-2'>
          <Button variant='grayGhost' className='p-2' aria-label='Back to scheduling settings' onClick={() => navigate(backPath)}>
            <i className='bi bi-arrow-left' />
          </Button>
          <div className='d-flex flex-column'>
            <span className='lead-small zocks-text-gray-500 text-uppercase'>Edit event type</span>
            <h4 className='mb-0 zocks-text-gray-900'>{eventType?.name ?? ''}</h4>
          </div>
        </Col>
      </Row>
      {isLoading || !eventType ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : (
        <EventTypeEditor
          key={eventType.id}
          eventType={eventType}
          accountSettings={accountSettings}
          locations={locations ?? []}
          activeTab={activeTab}
          disabled={disabled}
          onTabChange={handleTabChange}
        />
      )}
    </Container>
  )
}
